import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

function PrixMagasinModal({ 
  isOpen, 
  onClose, 
  productInfo, 
  itemId,
  movementId,
  defaultPrice = null,
  onSuccess 
}) {
  const [magasinSelected, setMagasinSelected] = useState('');
  const [prixReel, setPrixReel] = useState('');
  const [loading, setLoading] = useState(false);

  const parseNumericValue = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const normalized = typeof value === 'string' ? value.replace(',', '.') : value;
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const formatPrice = (value) => {
    const parsed = parseNumericValue(value);
    return parsed !== null && parsed > 0 ? `${parsed.toFixed(2)} €` : null;
  };

  const estimatedPriceValue =
    productInfo?.prix_resolved ??
    productInfo?.prix_moyen ??
    productInfo?.prix_estimation?.prix_moyen ??
    productInfo?.prix_estime ??
    null;
  const estimatedPriceLabel = formatPrice(estimatedPriceValue);
  const estimatedStore =
    productInfo?.magasin_moins_cher ||
    productInfo?.magasin_estime ||
    productInfo?.magasin_achat_estime ||
    null;
  const hasEstimatedHint = estimatedPriceLabel || estimatedStore;

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setMagasinSelected('');
      setPrixReel(defaultPrice !== null && defaultPrice !== undefined ? String(defaultPrice) : '');
    }
  }, [isOpen, defaultPrice]);

  const handleSave = async () => {
    if (!magasinSelected) {
      toast.error('Veuillez sélectionner un magasin');
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        item_id: itemId,
        magasin: magasinSelected.trim(),
        prix_reel: parseNumericValue(prixReel),
        movement_id: movementId || null
      };
      console.log('📤 PrixMagasinModal sending request:', requestData);
      
      await axios.post('/items/update-magasin-prix', requestData);

      if (prixReel && parseFloat(prixReel) > 0) {
        toast.success(`✅ Prix enregistré : ${parseFloat(prixReel).toFixed(2)}€ chez ${magasinSelected}`);
      } else {
        toast.success(`Magasin enregistré : ${magasinSelected}`);
      }

      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Erreur mise à jour magasin:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    onSuccess && onSuccess();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="text-lg">🏬 Où avez-vous acheté ce produit ?</CardTitle>
        </CardHeader>
        <CardContent className="max-h-[70vh] overflow-y-auto">
          <div className="space-y-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-blue-900">
                📦 {productInfo?.nom || 'Article'}
              </p>
            </div>

            {hasEstimatedHint && (
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                <p className="text-sm font-medium text-amber-900">Repere estime</p>
                {estimatedPriceLabel && (
                  <p className="text-sm text-amber-800 mt-1">
                    Prix moyen estime: <strong>{estimatedPriceLabel}</strong>
                  </p>
                )}
                {estimatedStore && (
                  <p className="text-sm text-amber-800">
                    Magasin le moins cher estime: <strong>{estimatedStore}</strong>
                  </p>
                )}
                <p className="text-xs text-amber-700 mt-2">
                  Le prix achete reste le prix reel lu sur ticket ou saisi par vous.
                </p>
              </div>
            )}

            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                🏬 Sélectionnez votre magasin
              </p>
              <div className="border rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
                {['Carrefour', 'Lidl', 'Auchan', 'Intermarché', 'Leclerc', 'Casino', 'Monoprix', 'Autre'].map((enseigne) => (
                  <div
                    key={enseigne}
                    className={`flex items-center justify-between p-2 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${
                      magasinSelected === enseigne ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                    }`}
                    onClick={() => setMagasinSelected(enseigne)}
                  >
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="magasin"
                        value={enseigne}
                        checked={magasinSelected === enseigne}
                        onChange={() => setMagasinSelected(enseigne)}
                        className="w-4 h-4"
                      />
                      <span className="font-medium text-gray-800">{enseigne}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>💵 Prix payé (optionnel)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="Ex: 3.70"
                value={prixReel}
                onChange={(e) => setPrixReel(e.target.value)}
              />
              <p className="text-xs text-gray-500">
                Ajoutez le prix réel pour améliorer nos estimations et alimenter l'historique
              </p>
            </div>

            {prixReel && parseFloat(prixReel) > 0 && (
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="text-sm text-green-800">
                  ✅ Prix : <strong>{parseFloat(prixReel).toFixed(2)} €</strong>
                </p>
                <p className="text-xs text-green-700 mt-1">
                  Cet achat sera enregistré dans votre historique
                </p>
              </div>
            )}

            <div className="flex space-x-2">
              <Button
                onClick={handleSave}
                disabled={!magasinSelected || loading}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                <CheckCircle size={16} className="mr-2" />
                {prixReel && parseFloat(prixReel) > 0 ? 'Enregistrer' : 'Confirmer'}
              </Button>
              <Button
                variant="outline"
                onClick={handleSkip}
                disabled={loading}
              >
                Passer
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default PrixMagasinModal;
